<script setup lang="ts">
  import { computed, onMounted, reactive, useTemplateRef, watch } from 'vue'
  import * as IslandConfig from '~shared/config/island'
  import { IslandPublicView, UpdateIslandDataPublicView } from '~shared/types/island'
  import ClampedIntegerInput from './ClampedIntegerInput.vue'

  // Valid Dodo Code characters: A-H, J-N, P-Y, 0-9 (excludes I, O, Z)
  const DODO_CODE_VALID = /^[A-HJ-NP-Y0-9]{5}$/i
  const DODO_DIGITS_VALID = /^[A-HJ-NP-Y0-9]{0,5}$/i

  const privacyOptions: Array<{ name: string; icon: string; value: IslandConfig.PrivacyOption }> = [
    { name: 'Twitch Sub', icon: 'fa7-brands-twitch', value: 'twitch-sub' },
    { name: 'Discord Only', icon: 'fa7-brands-discord', value: 'discord' },
    { name: 'Patreon Only', icon: 'fa7-brands-patreon', value: 'patreon' },
  ]

  const props = defineProps<{
    island?: IslandPublicView
  }>()

  const emit = defineEmits<{
    confirm: [updateData: UpdateIslandDataPublicView]
    cancel: []
  }>()

  const rootEl = useTemplateRef<HTMLDivElement>('rootEl')
  const state = reactive<UpdateIslandDataPublicView>({
    dodoCode: '',
    description: '',
    islandLimit: 0,
    queueLimit: 0,
    private: false,
    privacy: [],
  })

  const togglePrivacy = (privacyOption?: IslandConfig.PrivacyOption) => {
    if (!state.privacy) state.privacy = []

    if (!privacyOption) {
      state.privacy = []
      return
    }

    const index = state.privacy.indexOf(privacyOption)

    if (index === -1) {
      state.privacy.push(privacyOption)
      return
    }

    state.privacy.splice(index, 1)
  }

  const canSubmit = computed(
    () =>
      state.dodoCode &&
      DODO_CODE_VALID.test(state.dodoCode) &&
      state.islandLimit &&
      state.islandLimit >= 1 &&
      state.islandLimit <= 11 &&
      state.queueLimit &&
      state.queueLimit >= 1 &&
      state.queueLimit <= 100,
  )

  watch(
    () => state.dodoCode,
    (newVal, oldVal) => {
      if (!!newVal && !DODO_DIGITS_VALID.test(newVal)) state.dodoCode = oldVal
    },
  )

  onMounted(() => {
    state.dodoCode = props.island?.dodoCode || ''
    state.description = props.island?.description || ''
    state.islandLimit = props.island?.islandLimit || 0
    state.queueLimit = props.island?.queueLimit || 0
    state.private = props.island?.private || false
    state.privacy = props.island?.privacy || []
    rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
  })
</script>

<template>
  <div
    class="h-screen w-screen max-w-2xl rounded-xl border border-red-400/80 bg-purple-500 p-8 text-white shadow-2xl md:h-full md:w-full"
    ref="rootEl"
  >
    <div class="flex h-full flex-col">
      <div class="font-display flex h-12 justify-center">
        <h2 class="text-center text-3xl font-bold">Edit Island</h2>
      </div>

      <!-- Input Section -->
      <div class="my-4 flex flex-1 flex-col items-center gap-4 overflow-y-auto text-center">
        <h3 class="font-display mb-2 text-center text-xl font-bold">Dodo Code</h3>
        <div class="flex w-full justify-center">
          <input
            v-model="state.dodoCode"
            maxlength="5"
            type="text"
            class="w-1/2 self-center rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold uppercase focus:border-green-400 focus:outline-none"
            placeholder="A1B2C"
          />
        </div>
        <h3 class="font-display mb-2 text-center text-xl font-bold">Description</h3>
        <div class="flex w-full justify-center">
          <textarea
            v-model="state.description"
            maxlength="2048"
            class="h-24 w-full resize-none rounded-lg border-2 border-white/20 bg-white/5 p-4 text-white focus:border-green-400 focus:outline-none lg:h-32"
            placeholder="Write a message for your visitors..."
          ></textarea>
        </div>
        <div class="flex justify-center gap-8">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-bold opacity-80">Island Limit</label>
            <ClampedIntegerInput
              v-model.number="state.islandLimit"
              :min="0"
              :max="11"
              class="w-full rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold focus:border-green-400 focus:outline-none"
              placeholder="1"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-bold opacity-80">Queue Limit</label>
            <ClampedIntegerInput
              v-model.number="state.queueLimit"
              :min="0"
              :max="100"
              class="w-full rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold focus:border-green-400 focus:outline-none"
              placeholder="5"
            />
          </div>
        </div>
        <div class="flex flex-col justify-center gap-y-1">
          <p class="text-left text-xs">
            You will allow <b>{{ state.islandLimit }}</b> visitors on your island at the same time.
          </p>
          <p class="text-left text-xs">
            You will have a max of <b>{{ state.queueLimit }}</b> visitors in the queue.
          </p>
        </div>
        <h3 class="font-display mb-2 text-center text-xl font-bold">Privacy</h3>
        <div class="flex w-full justify-center">
          <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <button
              @click="state.private = !state.private"
              :class="[
                'flex h-24 w-24 flex-col items-center justify-center gap-2 rounded-xl border-2 transition-all',
                state.private
                  ? 'border-green-400 bg-green-950'
                  : 'border-white/20 bg-white/10 hover:border-white/40',
              ]"
            >
              <span class="text-3xl">
                <i-fa7-solid-eye-slash />
              </span>
              <span class="text-xs font-bold">Private</span>
            </button>
            <button
              v-for="opt in privacyOptions"
              :key="opt.value"
              @click="togglePrivacy(opt.value)"
              :class="[
                'flex h-24 w-24 flex-col items-center justify-center gap-2 rounded-xl border-2 transition-all',
                state.privacy && state.privacy.includes(opt.value)
                  ? 'border-green-400 bg-green-950'
                  : 'border-white/20 bg-white/10 hover:border-white/40',
              ]"
            >
              <!-- Icons placeholders since actual mdi icons might need importing or resoling -->
              <span class="text-3xl">
                <i-fa7-brands-twitch v-if="opt.value === 'twitch-sub'" />
                <i-fa7-brands-discord v-else-if="opt.value === 'discord'" />
                <i-fa7-brands-patreon v-else-if="opt.value === 'patreon'" />
              </span>
              <span class="text-xs font-bold">{{ opt.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Action -->
      <div class="flex h-14 justify-center gap-4">
        <button
          @click="emit('confirm', state)"
          :disabled="!canSubmit"
          class="cursor-pointer rounded-lg bg-green-400 px-8 py-2 font-bold text-black transition-colors hover:bg-green-500 disabled:cursor-default disabled:opacity-50"
        >
          Update
        </button>

        <button
          @click="emit('cancel')"
          class="bg-base-orange cursor-pointer rounded-lg px-8 py-2 font-bold text-black transition-colors hover:bg-red-500"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>
